import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import JSZip from 'jszip';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Download, Square, RefreshCw, Package, Layers, FileText, ChevronDown, ChevronRight } from 'lucide-react';

interface Study {
  id: string;
  user_id: string;
  query: string;
  total_runs: number;
  runs_completed: number;
  runs_failed: number;
  status: string;
  mode: 'fast' | 'spread';
  model: string;
  jsonl_path: string;
  estimated_cost_usd: number | null;
  actual_cost_usd: number;
  last_error: string | null;
  created_at: string;
  completed_at: string | null;
  spread_hours: number | null;
  cancel_requested_at: string | null;
}

const MODELS = [
  { value: 'gpt-4.1', label: 'gpt-4.1 (full)' },
  { value: 'gpt-4.1-mini-2025-04-14', label: 'gpt-4.1-mini' },
];

const MAX_BULK_QUERIES = 100;

const estimateCost = (model: string, totalRuns: number) => {
  const perCall = model.includes('mini') ? 0.005 : 0.012;
  return totalRuns * perCall;
};

const sanitizeQueryForFilename = (q: string) =>
  q.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'query';

const RepetitionStudy: React.FC = () => {
  const [submitMode, setSubmitMode] = useState<'single' | 'bulk'>('single');
  const [query, setQuery] = useState('');
  const [bulkQueries, setBulkQueries] = useState('');
  const [totalRuns, setTotalRuns] = useState(500);
  const [mode, setMode] = useState<'fast' | 'spread'>('fast');
  const [intervalSec, setIntervalSec] = useState(0.5);
  const [spreadHours, setSpreadHours] = useState(24);
  const [concurrency, setConcurrency] = useState(20);
  const [model, setModel] = useState('gpt-4.1');
  const [country, setCountry] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [studies, setStudies] = useState<Study[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const parsedBulkQueries = useMemo(
    () =>
      bulkQueries
        .split('\n')
        .map((q) => q.trim())
        .filter((q) => q.length > 0)
        .slice(0, MAX_BULK_QUERIES),
    [bulkQueries]
  );

  const loadStudies = async () => {
    const { data, error } = await supabase
      .from('repetition_studies')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200);
    if (error) {
      console.error(error);
      return;
    }
    setStudies((data || []) as Study[]);
  };

  useEffect(() => {
    loadStudies();
    const id = setInterval(loadStudies, 5000);
    return () => clearInterval(id);
  }, []);

  const validate = (): string | null => {
    if (totalRuns < 1 || totalRuns > 5000) return 'Total runs must be 1-5000';
    if (submitMode === 'single' && !query.trim()) return 'Query is required';
    if (submitMode === 'bulk') {
      if (parsedBulkQueries.length === 0) return 'Paste at least one query';
      if (bulkQueries.split('\n').filter((q) => q.trim()).length > MAX_BULK_QUERIES)
        return `Max ${MAX_BULK_QUERIES} queries per bulk submit`;
    }
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) {
        toast.error('Sign in required');
        return;
      }
      const userId = userData.user.id;
      const computedInterval =
        mode === 'spread' ? (spreadHours * 3600) / totalRuns : intervalSec;

      const queries = submitMode === 'single' ? [query.trim()] : parsedBulkQueries;

      const rows = queries.map((q) => {
        const jobId = crypto.randomUUID();
        return {
          id: jobId,
          user_id: userId,
          query: q,
          total_runs: totalRuns,
          mode,
          interval_seconds: computedInterval,
          batch_spacing_seconds: mode === 'fast' ? intervalSec : 0,
          concurrency: mode === 'fast' ? concurrency : 1,
          spread_hours: mode === 'spread' ? spreadHours : null,
          model,
          country: country || null,
          system_prompt: systemPrompt || null,
          jsonl_path: `${userId}/${jobId}.jsonl`,
          estimated_cost_usd: estimateCost(model, totalRuns),
          status: 'queued',
          next_run_at: new Date().toISOString(),
        };
      });

      const { error } = await supabase.from('repetition_studies').insert(rows);
      if (error) throw error;
      toast.success(
        queries.length === 1
          ? 'Study queued.'
          : `${queries.length} studies queued. Global cap of 400 in-flight throttles them automatically.`
      );
      if (submitMode === 'bulk') setBulkQueries('');
      else setQuery('');
      loadStudies();
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelStudy = async (id: string) => {
    const { error } = await supabase
      .from('repetition_studies')
      .update({ cancel_requested_at: new Date().toISOString() })
      .eq('id', id);
    if (error) toast.error(error.message);
    else {
      toast.success('Cancel requested — finishing current batch');
      loadStudies();
    }
  };

  const cancelMany = async (ids: string[]) => {
    if (ids.length === 0) {
      toast.error('Nothing selected');
      return;
    }
    const { error } = await supabase
      .from('repetition_studies')
      .update({ cancel_requested_at: new Date().toISOString() })
      .in('id', ids);
    if (error) toast.error(error.message);
    else {
      toast.success(`Cancel requested for ${ids.length} stud${ids.length === 1 ? 'y' : 'ies'}`);
      loadStudies();
    }
  };

  const fetchJsonl = async (id: string): Promise<Blob | null> => {
    const { data: sess } = await supabase.auth.getSession();
    const token = sess?.session?.access_token;
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/repetition-study-tick?action=download&id=${id}`;
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return blob.size === 0 ? null : blob;
  };

  const downloadJsonl = async (s: Study) => {
    try {
      const blob = await fetchJsonl(s.id);
      if (!blob) {
        toast.error('No data yet — JSONL has no completed runs');
        return;
      }
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `repetition-${sanitizeQueryForFilename(s.query)}-${s.id.slice(0, 8)}.jsonl`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Download failed');
    }
  };

  const downloadZip = async (targets: Study[], label: string) => {
    if (targets.length === 0) {
      toast.error('Nothing selected');
      return;
    }
    setBulkDownloading(true);
    const zip = new JSZip();
    let included = 0;
    let skipped = 0;
    try {
      // Fetch in parallel but cap concurrency to avoid hammering the function
      const CONC = 6;
      let i = 0;
      const worker = async () => {
        while (i < targets.length) {
          const idx = i++;
          const s = targets[idx];
          try {
            const blob = await fetchJsonl(s.id);
            if (!blob) {
              skipped++;
              continue;
            }
            const text = await blob.text();
            const fname = `${String(idx + 1).padStart(3, '0')}-${sanitizeQueryForFilename(s.query)}-${s.id.slice(0, 8)}.jsonl`;
            zip.file(fname, text);
            included++;
          } catch {
            skipped++;
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONC, targets.length) }, worker));

      if (included === 0) {
        toast.error('No completed runs in selected studies');
        return;
      }

      // manifest
      const manifest = targets.map((s) => ({
        id: s.id,
        query: s.query,
        model: s.model,
        mode: s.mode,
        status: s.status,
        runs_completed: s.runs_completed,
        runs_failed: s.runs_failed,
        total_runs: s.total_runs,
        actual_cost_usd: s.actual_cost_usd,
        created_at: s.created_at,
      }));
      zip.file('manifest.json', JSON.stringify(manifest, null, 2));

      const content = await zip.generateAsync({ type: 'blob' });
      const objUrl = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = objUrl;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `repetition-${label}-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success(`Downloaded ${included} JSONL${included === 1 ? '' : 's'}${skipped ? ` (${skipped} skipped, no data)` : ''}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ZIP build failed');
    } finally {
      setBulkDownloading(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(studies.map((s) => s.id)));
  const selectNone = () => setSelectedIds(new Set());
  const selectActive = () =>
    setSelectedIds(
      new Set(
        studies
          .filter((s) => s.status === 'running' || s.status === 'queued')
          .map((s) => s.id)
      )
    );

  const queryCount = submitMode === 'bulk' ? Math.max(parsedBulkQueries.length, 1) : 1;
  const cost = estimateCost(model, totalRuns) * queryCount;
  const interval = mode === 'spread' ? (spreadHours * 3600) / totalRuns : intervalSec;
  const totalCalls = totalRuns * queryCount;
  const selectedStudies = studies.filter((s) => selectedIds.has(s.id));
  const selectedActive = selectedStudies.filter(
    (s) => (s.status === 'running' || s.status === 'queued') && !s.cancel_requested_at
  );
  const allSelected = studies.length > 0 && selectedIds.size === studies.length;

  // Group studies by submission batch: same created_at within 10s window = one batch
  const groups = useMemo(() => {
    const sorted = [...studies].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const out: { key: string; label: string; createdAt: string; items: Study[] }[] = [];
    for (const s of sorted) {
      const t = new Date(s.created_at).getTime();
      const last = out[out.length - 1];
      if (last && Math.abs(new Date(last.createdAt).getTime() - t) <= 10_000) {
        last.items.push(s);
      } else {
        out.push({ key: s.created_at + '-' + s.id, label: '', createdAt: s.created_at, items: [s] });
      }
    }
    for (const g of out) {
      const d = new Date(g.createdAt);
      g.label =
        g.items.length === 1
          ? `Single · ${d.toLocaleString()}`
          : `Batch of ${g.items.length} · ${d.toLocaleString()}`;
    }
    return out;
  }, [studies]);

  const toggleGroupCollapse = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectGroup = (items: Study[], on: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const s of items) {
        if (on) next.add(s.id);
        else next.delete(s.id);
      }
      return next;
    });
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/grow-geo">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">Repetition Study</h1>
        </div>
        <Button variant="outline" size="sm" onClick={loadStudies}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>{submitMode === 'single' ? 'New study' : 'Bulk study (multi-query)'}</CardTitle>
            <div className="flex gap-2">
              <Button
                variant={submitMode === 'single' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSubmitMode('single')}
              >
                <FileText className="w-4 h-4 mr-1" /> Single
              </Button>
              <Button
                variant={submitMode === 'bulk' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSubmitMode('bulk')}
              >
                <Layers className="w-4 h-4 mr-1" /> Bulk study (multi-query)
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {submitMode === 'single' ? (
            <div>
              <Label>Query</Label>
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="best CRM" />
            </div>
          ) : (
            <div>
              <Label>Queries (one per line, max {MAX_BULK_QUERIES})</Label>
              <Textarea
                value={bulkQueries}
                onChange={(e) => setBulkQueries(e.target.value)}
                rows={8}
                placeholder={`best CRM\nbest accounting software\nbest project management tool`}
                className="font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {parsedBulkQueries.length} valid quer{parsedBulkQueries.length === 1 ? 'y' : 'ies'} detected
                {parsedBulkQueries.length === MAX_BULK_QUERIES && ' (cap reached)'}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label>Runs per query</Label>
              <Input
                type="number"
                min={1}
                max={5000}
                value={totalRuns}
                onChange={(e) => setTotalRuns(parseInt(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={mode} onValueChange={(v: 'fast' | 'spread') => setMode(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fast">Fast (back-to-back)</SelectItem>
                  <SelectItem value="spread">Spread across hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mode === 'fast' ? (
              <div>
                <Label>Spacing between batches (s)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min={0}
                  value={intervalSec}
                  onChange={(e) => setIntervalSec(parseFloat(e.target.value) || 0)}
                />
              </div>
            ) : (
              <div>
                <Label>Spread across (hours)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min={0.1}
                  value={spreadHours}
                  onChange={(e) => setSpreadHours(parseFloat(e.target.value) || 0)}
                />
              </div>
            )}
            <div>
              <Label>Model</Label>
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODELS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {mode === 'fast' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Concurrency per study (1–50)</Label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={concurrency}
                  onChange={(e) =>
                    setConcurrency(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))
                  }
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Global cap of 400 in-flight calls auto-throttles when multiple studies run together.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Country (optional, ISO code)</Label>
              <Input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="US, IL, etc." />
            </div>
            <div>
              <Label>System prompt (optional, overrides default)</Label>
              <Textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                rows={2}
                placeholder="Leave blank for default research-assistant prompt"
              />
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted rounded-md flex-wrap gap-3">
            <div className="text-sm space-y-1">
              <div>
                Estimated cost:{' '}
                <span className="font-mono font-semibold">${cost.toFixed(2)}</span>
                {submitMode === 'bulk' && (
                  <span className="text-muted-foreground">
                    {' '}
                    ({queryCount} queries × {totalRuns} runs = {totalCalls.toLocaleString()} calls)
                  </span>
                )}
              </div>
              <div className="text-muted-foreground">
                Interval: {interval.toFixed(1)}s/run ·{' '}
                {mode === 'spread'
                  ? `${spreadHours}h total per study`
                  : `~${((totalCalls * interval) / 60 / Math.max(1, Math.min(400, queryCount * concurrency))).toFixed(1)}min total (best case, throttled by 400 cap)`}
              </div>
            </div>
            <Button onClick={submit} disabled={submitting}>
              {submitting
                ? 'Submitting...'
                : submitMode === 'bulk'
                  ? `Start ${queryCount} stud${queryCount === 1 ? 'y' : 'ies'}`
                  : 'Start study'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>Studies ({studies.length})</CardTitle>
            <div className="flex gap-2 flex-wrap items-center">
              <div className="flex items-center gap-1 text-xs">
                <Button variant="ghost" size="sm" onClick={allSelected ? selectNone : selectAll}>
                  {allSelected ? 'Deselect all' : 'Select all'}
                </Button>
                <Button variant="ghost" size="sm" onClick={selectActive}>
                  Select running
                </Button>
                {selectedIds.size > 0 && (
                  <span className="text-muted-foreground px-2">
                    {selectedIds.size} selected
                  </span>
                )}
              </div>
              <Button
                variant="destructive"
                size="sm"
                disabled={selectedActive.length === 0}
                onClick={() => cancelMany(selectedActive.map((s) => s.id))}
              >
                <Square className="w-4 h-4 mr-1" />
                Cancel selected ({selectedActive.length})
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={selectedStudies.length === 0 || bulkDownloading}
                onClick={() => downloadZip(selectedStudies, `selected-${selectedStudies.length}`)}
              >
                <Package className="w-4 h-4 mr-1" />
                {bulkDownloading ? 'Zipping…' : `Download selected (${selectedStudies.length}) ZIP`}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={studies.length === 0 || bulkDownloading}
                onClick={() => downloadZip(studies, `all-${studies.length}`)}
              >
                <Package className="w-4 h-4 mr-1" /> All as ZIP
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {studies.length === 0 ? (
            <p className="text-sm text-muted-foreground">No studies yet.</p>
          ) : (
            <div className="space-y-4">
              {groups.map((g) => {
                const isCollapsed = collapsedGroups.has(g.key);
                const groupActive = g.items.filter(
                  (s) => (s.status === 'running' || s.status === 'queued') && !s.cancel_requested_at
                );
                const groupSelectedCount = g.items.filter((s) => selectedIds.has(s.id)).length;
                const allInGroupSelected = groupSelectedCount === g.items.length;
                const totalDone = g.items.reduce(
                  (acc, s) => acc + Math.min(s.runs_completed, s.total_runs),
                  0
                );
                const totalRunsAll = g.items.reduce((acc, s) => acc + s.total_runs, 0);
                const totalCost = g.items.reduce((acc, s) => acc + s.actual_cost_usd, 0);
                const groupPct = totalRunsAll > 0 ? (totalDone / totalRunsAll) * 100 : 0;
                const completedCount = g.items.filter((s) => s.status === 'completed').length;
                return (
                  <div key={g.key} className="border rounded-md">
                    <div className="flex items-center justify-between gap-3 p-3 bg-muted/30 rounded-t-md">
                      <button
                        type="button"
                        onClick={() => toggleGroupCollapse(g.key)}
                        className="flex items-center gap-2 min-w-0 flex-1 text-left"
                      >
                        {isCollapsed ? (
                          <ChevronRight className="w-4 h-4 shrink-0" />
                        ) : (
                          <ChevronDown className="w-4 h-4 shrink-0" />
                        )}
                        <input
                          type="checkbox"
                          className="cursor-pointer"
                          checked={allInGroupSelected}
                          ref={(el) => {
                            if (el)
                              el.indeterminate =
                                groupSelectedCount > 0 && !allInGroupSelected;
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => selectGroup(g.items, e.target.checked)}
                          aria-label="Select group"
                        />
                        <span className="font-medium text-sm truncate">{g.label}</span>
                        <Badge variant="outline" className="shrink-0">
                          {completedCount}/{g.items.length} done
                        </Badge>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {totalDone.toLocaleString()}/{totalRunsAll.toLocaleString()} runs · $
                          {totalCost.toFixed(2)}
                        </span>
                      </button>
                      <div className="flex gap-1 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={bulkDownloading}
                          onClick={() =>
                            downloadZip(g.items, `group-${g.items.length}-${g.createdAt.slice(0, 10)}`)
                          }
                          title="Download all in group as ZIP"
                        >
                          <Package className="w-3 h-3 mr-1" /> ZIP
                        </Button>
                        {groupActive.length > 0 && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => cancelMany(groupActive.map((s) => s.id))}
                            title="Cancel all active in group"
                          >
                            <Square className="w-3 h-3 mr-1" /> Cancel {groupActive.length}
                          </Button>
                        )}
                      </div>
                    </div>
                    {!isCollapsed && (
                      <div className="p-3 pt-2">
                        <Progress value={groupPct} className="h-1 mb-3" />
                        <div className="space-y-3">
                          {g.items.map((s) => {
                            const completedClamped = Math.min(s.runs_completed, s.total_runs);
                            const pct = (completedClamped / s.total_runs) * 100;
                            const isCancelling =
                              s.cancel_requested_at &&
                              !['cancelled', 'completed', 'failed'].includes(s.status);
                            const displayStatus = isCancelling ? 'cancelling' : s.status;
                            const isActive = s.status === 'running' || s.status === 'queued';
                            const avgPerRun =
                              completedClamped > 0 ? s.actual_cost_usd / completedClamped : 0;
                            const projected = avgPerRun > 0 ? avgPerRun * s.total_runs : 0;
                            const checked = selectedIds.has(s.id);
                            return (
                              <div key={s.id} className="border rounded-md p-3">
                                <div className="flex items-start justify-between gap-3 mb-2">
                                  <div className="flex items-start gap-3 min-w-0 flex-1">
                                    <input
                                      type="checkbox"
                                      className="mt-1 cursor-pointer"
                                      checked={checked}
                                      onChange={() => toggleSelect(s.id)}
                                      aria-label="Select for bulk download"
                                    />
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <span className="font-medium truncate">{s.query}</span>
                                        <Badge
                                          variant={
                                            s.status === 'completed'
                                              ? 'default'
                                              : s.status === 'failed' || s.status === 'cancelled'
                                                ? 'destructive'
                                                : isCancelling
                                                  ? 'outline'
                                                  : 'secondary'
                                          }
                                        >
                                          {displayStatus}
                                        </Badge>
                                        <Badge variant="outline">{s.mode}</Badge>
                                        <Badge variant="outline">{s.model}</Badge>
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        {completedClamped} / {s.total_runs} done · {s.runs_failed} failed · $
                                        {s.actual_cost_usd.toFixed(3)} of est $
                                        {s.estimated_cost_usd?.toFixed(2) ?? '?'}
                                        {completedClamped > 0 && (
                                          <>
                                            {' '}
                                            · ${avgPerRun.toFixed(4)}/run · projected ${projected.toFixed(2)}
                                          </>
                                        )}
                                      </div>
                                      {isCancelling && (
                                        <div className="text-xs text-muted-foreground mt-1">
                                          Finishing current batch…
                                        </div>
                                      )}
                                      {s.last_error && (
                                        <div className="text-xs text-destructive mt-1 truncate">
                                          Last error: {s.last_error}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex gap-1 shrink-0">
                                    <Button size="sm" variant="outline" onClick={() => downloadJsonl(s)}>
                                      <Download className="w-3 h-3" />
                                    </Button>
                                    {isActive && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => cancelStudy(s.id)}
                                        disabled={!!isCancelling}
                                      >
                                        <Square className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                <Progress value={pct} className="h-2" />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RepetitionStudy;
